import type { DriverInput, KartState } from "./types";
import { DEFAULT_RACE_CLASS, getRaceClassProfile, type RaceClass } from "./balance";
import { DEFAULT_TRACK_ID, distanceToShortcut, getRampDefinitions, getShortcutDefinition, getShortcutGapAt, getSignatureZoneDefinition, getTrackHalfWidth, getTrackLength, projectToTrack, sampleTrack, sourceSampleIndexAtDistance, usesSourceIndexedCursor, wrappedDistanceDelta, type ClassicJumpRuntime, type RampDefinition } from "./track";
import { getKartWeightProfile } from "../kartBuild";
import { applySideBumpBoost } from "./contactBoost";
import { CUSTOM_DIP_SPEED_RATIO, isInsideCustomTrackDip } from "./customTrackDips";
import { getClassicTrackRules } from "./track";
import { advanceClassicCheckpoint, classicCompletedLaps } from "./classicProgress";
import { stepVehicleDynamics } from "./vehicleDynamics";
export { getDriftTier } from "./vehicleDynamics";
// Match the course's physical outer edge to the detailed rails while keeping a
// narrow, driveable kerb/runoff strip before contact and recovery.
export const OFF_ROAD_BAND = 1.9;
export const DEEP_OFF_ROAD_THRESHOLD = OFF_ROAD_BAND;
export const OFF_ROAD_RECOVERY_DELAY = 2.8;
export const STUCK_RECOVERY_DELAY = 3.2;
export const WRONG_WAY_RECOVERY_DELAY = 4;
export const RECOVERY_DURATION = 1.15;
/** How far below the missing floor a kart drops before the marshal resets it. */
export const GAP_FALL_DEPTH = 7;
/** Maximum interruption applied by any hit source before control returns. */
export const SPIN_LOCKOUT_DURATION = 0.85;
/** The final part of a hit is steerable, but only with reduced authority. */
export const SPIN_LATE_STEERING_WINDOW = 0.22;
/** Keeps a released kart settled through the next bend while control returns. */
export const SPIN_RECOVERY_ASSIST_DURATION = 0.65;
const SPIN_LATE_STEER_SCALE = 0.32;

function wrapAngle(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle));
}

function applyCustomDipCompression(kart: KartState, trackId: string, distance: number, normalSpeed: number): void {
  if (kart.airborne || !isInsideCustomTrackDip(distance, trackId)) return;
  const speed = Math.hypot(kart.velocityX, kart.velocityZ);
  const cap = normalSpeed * CUSTOM_DIP_SPEED_RATIO;
  if (speed > cap) {
    kart.velocityX *= cap / speed;
    kart.velocityZ *= cap / speed;
    kart.speed = cap;
  }
}

function forwardSampleDistance(sampleIndex: number, targetIndex: number, sampleCount: number): number {
  return ((sampleIndex - targetIndex) % sampleCount + sampleCount) % sampleCount;
}

function classicJumpCandidate(ramps: readonly RampDefinition[], sampleIndex: number | undefined): RampDefinition | null {
  if (sampleIndex === undefined) return null;
  for (const ramp of ramps) {
    const profile = ramp.classic;
    if (!profile || ramp.surface !== "main") continue;
    const forward = forwardSampleDistance(sampleIndex, profile.sampleIndex, profile.sampleCount);
    if (forward < profile.triggerWindowSamples) return ramp;
  }
  return null;
}

function updateClassicJumpLatch(kart: KartState, ramps: readonly RampDefinition[], sampleIndex: number | undefined): void {
  const jumped = kart.classicJumpedSampleIndex;
  if (jumped === null || jumped === undefined || sampleIndex === undefined) return;
  const ramp = ramps.find((candidate) => candidate.classic?.sampleIndex === jumped && candidate.surface === "main");
  if (!ramp?.classic) { kart.classicJumpedSampleIndex = null; return; }
  const { sampleCount, rearmWindowSamples } = ramp.classic;
  const forward = forwardSampleDistance(sampleIndex, jumped, sampleCount);
  if (forward > rearmWindowSamples && forward < sampleCount - rearmWindowSamples) kart.classicJumpedSampleIndex = null;
}

function applyClassicLandingSpeed(kart: KartState, scale: number): void {
  kart.velocityX *= scale;
  kart.velocityZ *= scale;
  kart.speed = Math.hypot(kart.velocityX, kart.velocityZ);
}

function recoverKart(kart: KartState, trackId: string): void {
  const classic = getClassicTrackRules(trackId);
  if (classic) {
    const next = kart.classicNextCheckpoint ?? 0;
    const checkpoint = classic.checkpoints[(next + classic.checkpoints.length - 1) % classic.checkpoints.length];
    kart.classicSampleIndex = checkpoint;
    // Recovery holds this pose for over a second. Do not retain the old
    // segment fraction while the kart is back at its credited gate.
    kart.classicProgress = kart.classicGatesPassed ?? 0;
    kart.safeDistance = Math.floor(((kart.classicGatesPassed ?? 0) - 1) / classic.checkpoints.length) * classic.length + classic.samples[checkpoint].distance;
  }
  const frame = sampleTrack(kart.safeDistance, trackId);
  // Custom source-jump tracks do not have classic checkpoints, but they still
  // need a fresh local-CI anchor after recovery so the next crossover cannot
  // steal the projection branch.  Classic recovery keeps its checkpoint
  // anchor above; both paths deliberately clear the jump latch below, which
  // is the existing v2 recovery safety rule.
  if (!classic && usesSourceIndexedCursor(trackId)) {
    const sourceIndex = sourceSampleIndexAtDistance(trackId, kart.safeDistance);
    if (sourceIndex !== undefined) kart.classicSampleIndex = sourceIndex;
  }
  kart.x = frame.position.x;
  kart.y = frame.position.y + 0.14;
  kart.z = frame.position.z;
  kart.yaw = Math.atan2(frame.tangent.x, frame.tangent.z);
  kart.distance = kart.safeDistance;
  kart.lateral = 0;
  kart.velocityX = 0;
  kart.velocityZ = 0;
  kart.speed = 0;
  kart.driftCharge = 0;
  kart.driftDirection = 0;
  kart.boostTime = 0;
  kart.draftCharge = 0;
  kart.draftBoostTime = 0;
  kart.draftCooldown = 0;
  kart.draftTargetId = null;
  kart.spinTime = 0;
  kart.spinRecoveryTime = 0;
  kart.verticalVelocity = 0;
  kart.airborne = false;
  kart.airTime = 0;
  kart.trickQueued = false;
  kart.classicJumpedSampleIndex = null;
  kart.onShortcut = false;
  kart.surface = "road";
  kart.offRoadTime = 0;
  kart.wrongWayTime = 0;
  kart.recoveryTime = RECOVERY_DURATION;
  kart.recoveryCount += 1;
  kart.signatureZoneId = null;
  kart.signatureZoneTime = 0;
}

export function isKartInsideSignatureZone(kart: Pick<KartState, "distance" | "lateral" | "surface">, trackId = DEFAULT_TRACK_ID): boolean {
  const zone = getSignatureZoneDefinition(trackId);
  return zone !== null && kart.surface !== "offroad"
    && Math.abs(wrappedDistanceDelta(kart.distance, zone.distance, trackId)) <= zone.halfLength
    && Math.abs(kart.lateral) <= zone.halfWidth;
}

export function stepKart(kart: KartState, input: DriverInput, dt: number, trackId = DEFAULT_TRACK_ID, raceClass: RaceClass = DEFAULT_RACE_CLASS, paceAssist = 0, steeringScale = 1): void {
  const classic = getClassicTrackRules(trackId);
  const previousClassicSampleIndex = kart.classicSampleIndex;
  const tuning = getRaceClassProfile(raceClass);
  const build = getKartWeightProfile(kart.weightClass);
  paceAssist = Math.max(-0.08, Math.min(0.12, paceAssist));
  steeringScale = Math.max(0.75, Math.min(1.35, steeringScale));
  kart.itemDodgeTime = Math.max(0, kart.itemDodgeTime - dt);
  if (kart.recoveryTime > 0) {
    kart.recoveryTime = Math.max(0, kart.recoveryTime - dt);
    const frame = sampleTrack(kart.safeDistance, trackId);
    kart.x = frame.position.x; kart.y = frame.position.y + 0.14; kart.z = frame.position.z;
    kart.yaw = Math.atan2(frame.tangent.x, frame.tangent.z);
    kart.distance = kart.safeDistance; kart.lateral = 0; kart.onShortcut = false; kart.surface = "road";
    kart.velocityX = kart.recoveryTime <= 0 ? frame.tangent.x * 9 : 0;
    kart.velocityZ = kart.recoveryTime <= 0 ? frame.tangent.z * 9 : 0;
    kart.speed = kart.recoveryTime <= 0 ? 9 : 0;
    return;
  }
  const startProjection = projectToTrack(kart.x, kart.z, trackId, kart.y - 0.14, kart.distance, kart.classicSampleIndex);
  const startOutside = Math.max(0, Math.abs(startProjection.lateral) - startProjection.halfWidth);
  const offRoadRatio = kart.airborne ? 0 : Math.max(0, Math.min(1, startOutside / OFF_ROAD_BAND));
  const signatureZone = getSignatureZoneDefinition(trackId);
  const signatureActive = signatureZone !== null && isKartInsideSignatureZone(kart, trackId);
  const enteringSignatureZone = signatureActive && kart.signatureZoneId !== signatureZone.id;
  if (signatureActive) {
    kart.signatureZoneId = signatureZone.id;
    kart.signatureZoneTime += dt;
    if (enteringSignatureZone && kart.signatureZoneLastLap !== kart.lap) {
      kart.signatureZoneLastLap = kart.lap;
      kart.signatureZoneTriggerCount += 1;
      if (signatureZone.boostSeconds > 0) kart.boostTime = Math.max(kart.boostTime, signatureZone.boostSeconds);
      if (signatureZone.launchVelocity > 0 && !kart.airborne) {
        kart.airborne = true;
        kart.verticalVelocity = Math.max(kart.verticalVelocity, signatureZone.launchVelocity);
        kart.airTime = 0;
        kart.trickQueued = false;
      }
    }
  } else {
    kart.signatureZoneId = null;
    kart.signatureZoneTime = 0;
  }
  if (kart.spinTime > 0) {
    // Item, hazard, and course-event producers deliberately have their own
    // hit strengths. Keep the player-facing interruption bounded here so a
    // newly added producer cannot silently create a longer lockout.
    kart.spinTime = Math.min(SPIN_LOCKOUT_DURATION, kart.spinTime);
    kart.spinRecoveryTime = 0;
    kart.spinTime = Math.max(0, kart.spinTime - dt);
    const lateSteering = kart.spinTime > 0 && kart.spinTime <= SPIN_LATE_STEERING_WINDOW;
    if (kart.spinTime > 0) {
      kart.yaw = wrapAngle(kart.yaw + dt * 8.5);
      kart.velocityX *= Math.exp(-dt * 2.8);
      kart.velocityZ *= Math.exp(-dt * 2.8);
    } else {
      // A hit is a bounded interruption, not a second steering challenge. The
      // old fixed angular speed ended different hit durations at arbitrary
      // headings (often sideways) and AI then hid the problem with its higher
      // steering authority. Hand every controller back the same usable pose.
      const recoveryFrame = projectToTrack(kart.x, kart.z, trackId, kart.y - 0.14, kart.distance, kart.classicSampleIndex);
      const releaseSpeed = tuning.maxSpeed * build.maxSpeed * 0.28;
      kart.yaw = Math.atan2(recoveryFrame.tangent.x, recoveryFrame.tangent.z);
      kart.velocityX = recoveryFrame.tangent.x * releaseSpeed;
      kart.velocityZ = recoveryFrame.tangent.z * releaseSpeed;
      kart.speed = releaseSpeed;
      kart.driftCharge = 0;
      kart.driftDirection = 0;
      kart.spinRecoveryTime = SPIN_RECOVERY_ASSIST_DURATION;
    }
    // Let a player or autonomous controller influence only the tail of the
    // spin. The ordinary shared steering path applies the same reduced input
    // for every controller, while the release branch above still guarantees
    // an aligned heading and tangent velocity at the end.
    input = { ...input, throttle: 0, brake: 0, steer: lateSteering ? input.steer * SPIN_LATE_STEER_SCALE : 0, drift: false };
  }
  const steeringTowardRoad = startOutside > 0 && input.steer * Math.sign(startProjection.lateral) < -0.05;
  stepVehicleDynamics(kart, input, dt, {
    offRoadRatio,
    startOutside,
    lateral: startProjection.lateral,
    signatureSpeedScale: signatureActive ? signatureZone.speedScale : 1,
    signatureGripScale: signatureActive ? signatureZone.gripScale : 1,
    signatureSteeringScale: signatureActive ? signatureZone.steeringScale : 1,
    raceClass,
    paceAssist,
    steeringScale,
  });

  let projection = constrainKartToOuterBand(kart, trackId);
  let outside = Math.max(0, Math.abs(projection.lateral) - projection.halfWidth);
  const groundY = projection.position.y + 0.14;
  applyCustomDipCompression(kart, trackId, projection.distance, tuning.maxSpeed * build.maxSpeed);
  // A shortcut gap has no floor. While the kart is over one, the ground is
  // effectively far below: a kart that arrives without enough launch speed
  // keeps falling, and once it is clearly beneath the deck the marshal resets
  // it to the last solid ground it drove on. This is the skill gate that makes
  // a secret route earn its saving.
  const gap = getShortcutGapAt(kart.distance, trackId);
  const shortcut = gap ? getShortcutDefinition(trackId) : null;
  // Surface classification deliberately turns the narrow shoulder beside a
  // shortcut into "offroad". It must not conjure a bridge across a missing
  // deck span: the whole physical shortcut width, including its runoff band,
  // remains floorless over a gap.
  const overGap = gap !== null
    && shortcut !== null
    && distanceToShortcut(kart.x, kart.z, trackId) <= shortcut.halfWidth + OFF_ROAD_BAND;
  const floorY = overGap ? groundY - GAP_FALL_DEPTH - 1 : groundY;
  const ramps = getRampDefinitions(trackId);
  const classicProfile = ramps.find((ramp) => ramp.classic !== undefined)?.classic;
  const wasAirborne = kart.airborne;
  updateClassicJumpLatch(kart, ramps, projection.sampleIndex);
  if (kart.airborne) {
    kart.airTime += dt;
    if (input.drift && kart.airTime > 0.1) kart.trickQueued = true;
    // Classic and source-indexed custom jumps store motion in source units. Their
    // 0.125-metre conversion is explicit in the ramp metadata; authored and
    // shortcut ramps retain the existing v2 metre-native gravity below.
    const gravity = classicProfile?.gravity ?? 30;
    kart.verticalVelocity -= gravity * dt;
    kart.y += kart.verticalVelocity * dt;
    if (overGap && kart.y <= groundY - GAP_FALL_DEPTH) { recoverKart(kart, trackId); return; }
    if (kart.y <= floorY && kart.verticalVelocity <= 0) {
      kart.y = floorY;
      kart.verticalVelocity = 0;
      kart.airborne = false;
      kart.airTime = 0;
      if (wasAirborne && classicProfile) applyClassicLandingSpeed(kart, classicProfile.landingSpeedScale);
      if (kart.trickQueued) kart.boostTime = Math.max(kart.boostTime, 0.8);
      kart.trickQueued = false;
      applyCustomDipCompression(kart, trackId, projection.distance, tuning.maxSpeed * build.maxSpeed);
    }
  } else if (overGap) {
    // Rolled onto the gap without a launch: start falling from deck height.
    kart.airborne = true;
    kart.verticalVelocity = Math.min(0, kart.verticalVelocity);
    kart.airTime = 0;
    kart.trickQueued = false;
  } else {
    kart.y = groundY;
    const classic = classicJumpCandidate(ramps, projection.sampleIndex);
    // Legacy jump detection is source-indexed and intentionally has no ramp
    // width test. Keep the v2 physical road boundary as the one compatible
    // lateral guard: the visible loose shoulder is not a disconnected ramp.
    const classicRoadLateral = Math.abs(projection.lateral) <= getTrackHalfWidth(trackId);
    const rampSurface = kart.surface === "shortcut" ? "shortcut" : "main";
    const ramp = classic
      ? null
      : kart.surface !== "offroad"
        ? ramps.find((candidate) => candidate.classic === undefined && candidate.surface === rampSurface && Math.abs(wrappedDistanceDelta(kart.distance, candidate.distance, trackId)) <= candidate.halfLength && Math.abs(kart.lateral) <= candidate.halfWidth)
        : undefined;
    if (classic?.classic && kart.classicJumpedSampleIndex !== classic.classic.sampleIndex && classicRoadLateral && kart.speed > classic.classic.launchSpeedThreshold) {
      kart.classicJumpedSampleIndex = classic.classic.sampleIndex;
      kart.airborne = true;
      kart.verticalVelocity = classic.classic.launchVelocityBase + kart.speed * classic.classic.launchVelocityPerSpeed;
      kart.airTime = 0;
      kart.trickQueued = false;
      kart.y += 0.08;
    } else if (ramp && kart.rampCooldown <= 0 && kart.speed > 16) {
      kart.airborne = true;
      kart.verticalVelocity = 10 + Math.min(4, kart.speed * 0.07);
      kart.airTime = 0;
      kart.trickQueued = false;
      kart.rampCooldown = 2;
      kart.y += 0.08;
    }
  }
  const trackYaw = Math.atan2(projection.tangent.x, projection.tangent.z);
  if (kart.spinRecoveryTime > 0 && !kart.airborne) {
    // The exact release pose is not enough on a curved road: without this
    // short shared settling window a neutral kart immediately travels along
    // the old tangent and looks sideways again before the player can react.
    // Keep normal steering live by biasing the assisted heading toward the
    // player's requested direction, then fade the aid out quickly.
    kart.spinRecoveryTime = Math.max(0, kart.spinRecoveryTime - dt);
    const steeringBias = input.steer * 0.14;
    const targetYaw = wrapAngle(trackYaw + steeringBias);
    const headingBlend = 1 - Math.exp(-dt * 10);
    kart.yaw = wrapAngle(kart.yaw + wrapAngle(targetYaw - kart.yaw) * headingBlend);
    const travelSpeed = Math.hypot(kart.velocityX, kart.velocityZ);
    const targetVelocityX = Math.sin(kart.yaw) * travelSpeed;
    const targetVelocityZ = Math.cos(kart.yaw) * travelSpeed;
    const velocityBlend = 1 - Math.exp(-dt * 8);
    kart.velocityX += (targetVelocityX - kart.velocityX) * velocityBlend;
    kart.velocityZ += (targetVelocityZ - kart.velocityZ) * velocityBlend;
  }
  if (kart.speed < 1.5 && Math.abs(wrapAngle(kart.yaw - trackYaw)) > Math.PI * 0.8) kart.yaw = trackYaw;
  const progressSpeed = kart.velocityX * projection.tangent.x + kart.velocityZ * projection.tangent.z;
  if (!kart.airborne && kart.surface === "offroad") kart.offRoadTime += dt;
  else kart.offRoadTime = Math.max(0, kart.offRoadTime - dt * 2.5);
  if (!kart.airborne && progressSpeed < -4 && kart.speed > 6) kart.wrongWayTime += dt;
  else kart.wrongWayTime = Math.max(0, kart.wrongWayTime - dt * 2);
  // A shortcut is a committed gamble. Keep the marshal checkpoint on the main
  // road so missing its jump costs the attempted cut instead of banking the
  // shortcut's mapped course distance and awarding a huge progress skip.
  if (!classic && !kart.airborne && projection.surface === "main" && kart.surface === "road" && progressSpeed > -0.5 && Math.abs(kart.lateral) <= projection.halfWidth - 0.75) kart.safeDistance = kart.distance;
  // The whole loose-surface band is driveable. Do not rescue a player who is
  // actively steering inward at the physical edge; Heavy karts especially
  // need that extra moment to regain tyre bite under their own input.
  const deeplyLost = outside >= DEEP_OFF_ROAD_THRESHOLD - 1e-3 && !steeringTowardRoad && kart.offRoadTime >= OFF_ROAD_RECOVERY_DELAY;
  const stuckOffRoad = kart.surface === "offroad" && kart.speed < 2.5 && kart.offRoadTime >= STUCK_RECOVERY_DELAY;
  if (deeplyLost || stuckOffRoad || kart.wrongWayTime >= WRONG_WAY_RECOVERY_DELAY) recoverKart(kart, trackId);
  if (classic && kart.recoveryTime <= 0 && previousClassicSampleIndex !== undefined) {
    const previousGates = kart.classicGatesPassed ?? 0;
    advanceClassicCheckpoint(kart, classic, previousClassicSampleIndex);
    kart.lap = classicCompletedLaps(kart, classic) + 1;
    if (kart.classicGatesPassed !== previousGates) {
      const checkpoint = classic.checkpoints[((kart.classicNextCheckpoint ?? 0) + classic.checkpoints.length - 1) % classic.checkpoints.length];
      kart.safeDistance = Math.floor(((kart.classicGatesPassed ?? 0) - 1) / classic.checkpoints.length) * classic.length + classic.samples[checkpoint].distance;
    }
  }
}

export function refreshKartTrackState(kart: KartState, trackId = DEFAULT_TRACK_ID, constrainToRoad = false, updateClassicIndex = true): ReturnType<typeof projectToTrack> {
  const trackLength = getTrackLength(trackId);
  let projection = projectToTrack(kart.x, kart.z, trackId, kart.y - 0.14, kart.distance, kart.classicSampleIndex);
  const outside = Math.abs(projection.lateral) - projection.halfWidth;
  if (constrainToRoad && outside > 0) {
    const side = Math.sign(projection.lateral);
    kart.x -= projection.normal.x * side * outside;
    kart.z -= projection.normal.z * side * outside;
    const outwardVelocity = kart.velocityX * projection.normal.x * side + kart.velocityZ * projection.normal.z * side;
    if (outwardVelocity > 0) {
      kart.velocityX -= projection.normal.x * side * outwardVelocity * 1.55;
      kart.velocityZ -= projection.normal.z * side * outwardVelocity * 1.55;
    }
    kart.velocityX *= 0.82;
    kart.velocityZ *= 0.82;
    projection = projectToTrack(kart.x, kart.z, trackId, kart.y - 0.14, kart.distance, kart.classicSampleIndex);
  }

  const previousWrapped = ((kart.distance % trackLength) + trackLength) % trackLength;
  let progressDelta = projection.distance - previousWrapped;
  if (progressDelta > trackLength * 0.5) progressDelta -= trackLength;
  if (progressDelta < -trackLength * 0.5) progressDelta += trackLength;
  kart.distance += Math.max(-3, Math.min(3, progressDelta));
  if (updateClassicIndex && kart.classicSampleIndex !== undefined && projection.sampleIndex !== undefined) kart.classicSampleIndex = projection.sampleIndex;
  kart.lateral = projection.lateral;
  const currentOutside = Math.max(0, Math.abs(projection.lateral) - projection.halfWidth);
  kart.onShortcut = projection.surface === "shortcut" && currentOutside <= 1e-6;
  kart.surface = currentOutside > 1e-6 ? "offroad" : kart.onShortcut ? "shortcut" : "road";
  kart.speed = Math.hypot(kart.velocityX, kart.velocityZ);
  return projection;
}

export function constrainKartToOuterBand(kart: KartState, trackId = DEFAULT_TRACK_ID, updateClassicIndex = true): ReturnType<typeof projectToTrack> {
  let projection = refreshKartTrackState(kart, trackId, false, updateClassicIndex);
  const outside = Math.max(0, Math.abs(projection.lateral) - projection.halfWidth);
  if (outside <= OFF_ROAD_BAND) return projection;
  const side = Math.sign(projection.lateral) || 1;
  const correction = outside - OFF_ROAD_BAND;
  kart.x -= projection.normal.x * side * correction;
  kart.z -= projection.normal.z * side * correction;
  const outwardVelocity = kart.velocityX * projection.normal.x * side + kart.velocityZ * projection.normal.z * side;
  if (outwardVelocity > 0) {
    kart.velocityX -= projection.normal.x * side * outwardVelocity * 1.35;
    kart.velocityZ -= projection.normal.z * side * outwardVelocity * 1.35;
  }
  kart.velocityX *= 0.74;
  kart.velocityZ *= 0.74;
  projection = refreshKartTrackState(kart, trackId, false, updateClassicIndex);
  return projection;
}

export function resolveKartCollisions(karts: KartState[], trackId = DEFAULT_TRACK_ID): Set<KartState> {
  const minimumDistance = 3.35;
  const classic = getClassicTrackRules(trackId);
  const affected = new Set<KartState>();
  for (let i = 0; i < karts.length; i += 1) {
    for (let j = i + 1; j < karts.length; j += 1) {
      const a = karts[i];
      const b = karts[j];
      if (a.ghostTime > 0 || b.ghostTime > 0) continue;
      if (classic && a.classicSampleIndex !== undefined && b.classicSampleIndex !== undefined) {
        const separation = Math.abs(a.classicSampleIndex - b.classicSampleIndex);
        if (Math.min(separation, classic.samples.length - 1 - separation) > 60) continue;
      }
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const distance = Math.hypot(dx, dz);
      if (Math.abs(a.y - b.y) > 2.2 || distance >= minimumDistance || distance < 1e-5) continue;
      affected.add(a);
      affected.add(b);
      const nx = dx / distance;
      const nz = dz / distance;
      const massA = getKartWeightProfile(a.weightClass).mass;
      const massB = getKartWeightProfile(b.weightClass).mass;
      const totalMass = massA + massB;
      applySideBumpBoost(a, b);
      const correction = minimumDistance - distance;
      a.x -= nx * correction * massB / totalMass;
      a.z -= nz * correction * massB / totalMass;
      b.x += nx * correction * massA / totalMass;
      b.z += nz * correction * massA / totalMass;
      const relative = (b.velocityX - a.velocityX) * nx + (b.velocityZ - a.velocityZ) * nz;
      if (relative < 0) {
        const impulse = -relative * 0.72 / (1 / massA + 1 / massB);
        a.velocityX -= nx * impulse / massA;
        a.velocityZ -= nz * impulse / massA;
        b.velocityX += nx * impulse / massB;
        b.velocityZ += nz * impulse / massB;
      }
    }
  }
  return affected;
}

export function createGridTransform(distance: number, lateral: number, trackId = DEFAULT_TRACK_ID): Pick<KartState, "x" | "y" | "z" | "yaw"> {
  const frame = sampleTrack(distance, trackId);
  return {
    x: frame.position.x + frame.normal.x * lateral,
    y: frame.position.y + 0.14,
    z: frame.position.z + frame.normal.z * lateral,
    yaw: Math.atan2(frame.tangent.x, frame.tangent.z),
  };
}
